/**
 * RM-359 — the member-facing words about RM's review of a request, in one
 * place (the booking card, the request view, the request dialog and Review &
 * Submit all say the same thing).
 *
 * Kapil and the product advisor's wording (2026-10-01):
 *   - an unfinished request is "Draft · not submitted" — being in Bookings
 *     does not mean it was submitted;
 *   - "RM reviews this request before it's sent to {restaurant}";
 *   - "RM requires a change before it's sent" (required, not a suggestion).
 */

export const DRAFT_LABEL = 'Draft · not submitted'

/** "NAIA", "NAIA and Bavette's", "NAIA, Bavette's, Aba". */
export function joinNames(names) {
  const list = (names || []).map(n => (typeof n === 'string' ? n : n?.name)).filter(Boolean)
  if (!list.length) return ''

  return list.length > 2 ? list.join(', ') : list.join(' and ')
}

/** Every restaurant on a request, the preferred one first. */
export function requestedNames(reservation) {
  if (!reservation) return []

  return [reservation.preferredRestaurant, ...((reservation.alternateRestaurants || []).filter(n => typeof n === 'string'))].filter(Boolean)
}

/** "RM reviews this request before it's sent to NAIA" ("inquiry" for group/private dining). */
export function reviewLine(names, { noun = 'request' } = {}) {
  return `RM reviews this ${noun} before it's sent to ${joinNames(names) || 'the restaurant'}`
}

// Why a request is back in Draft (Reservation.approvalFeedback.kind, set by
// lib/reservationApproval.FEEDBACK_KINDS on the server).
const RETURNED_LABELS = {
  changes: 'RM requires a change before it\'s sent',
  declined: 'RM wasn\'t able to send this request',
  expired: 'Not reviewed in time, so it wasn\'t sent',
  hold_expired: 'Card hold expired - submit again to authorize a new hold',
}

/**
 * The note on a Draft that RM returned: { label, text } — or null for an
 * ordinary draft. `text` is RM's own words, when RM wrote any.
 */
export function returnedDraftNote(reservation) {
  const feedback = reservation?.approvalFeedback
  if (!reservation || reservation.status !== 'Draft' || !feedback) return null
  const kind = feedback.kind || (feedback.text ? 'changes' : null)
  if (!kind || !RETURNED_LABELS[kind]) return null

  return { kind, label: RETURNED_LABELS[kind], text: feedback.text || '' }
}
